import React from "react";
import { Tooltip } from "@mui/material";
import { formatTime } from "../helpers/dateFormat";

export interface AppointmentTimeProps {
  /** Start of the thing being displayed — a slot, or the appointment itself. */
  start?: string | Date | null;
  /** Its end. Not rendered; it is the tooltip's job now. */
  end?: string | Date | null;
  /**
   * The enclosing appointment, when what is displayed is one slot of it. Shown
   * in parentheses whenever it starts at a different time from `start`.
   */
  appointmentStart?: string | Date | null;
  appointmentEnd?: string | Date | null;
  /** What `start` refers to, for the tooltip's first line. */
  label?: string;
  className?: string;
}

const at = (value?: string | Date | null): string | undefined =>
  value ? formatTime(value instanceof Date ? value.toISOString() : value) : undefined;

const range = (from?: string, to?: string): string =>
  from && to ? `${from} – ${to}` : from ?? to ?? "—";

/**
 * The time of an appointment or of one of its slots.
 *
 * **Start only.** The end time was dropped from the display: it costs a
 * separator and four digits on every row, and it is almost never the thing
 * being scanned for. It lives in the tooltip instead, along with the full
 * appointment range.
 *
 * **The parenthesised time is retained**, because it carries information the
 * start alone cannot: an appointment can hold more than one slot, so when only
 * one of them is displayed (the Rx view shows just that Rx's slot) the moment
 * the patient actually arrives may be earlier than the treatment shown. That
 * divergent time is what the parentheses hold.
 *
 * The tooltip also *explains* the parentheses. Nothing in the display said what
 * a bare second time meant, so it was only legible to someone who already knew.
 */
export const AppointmentTime: React.FC<AppointmentTimeProps> = ({
  start,
  end,
  appointmentStart,
  appointmentEnd,
  label = "Behandlung",
  className,
}) => {
  const startAt = at(start);
  const endAt = at(end);
  const apptStartAt = at(appointmentStart);
  const apptEndAt = at(appointmentEnd);

  if (!startAt) {
    return <span className={className}>nicht geplant</span>;
  }

  // Only divergence is worth showing — an appointment that starts when its slot
  // starts adds nothing by repeating itself.
  const diverges = Boolean(apptStartAt && apptStartAt !== startAt);

  const tooltip = (
    <>
      <div>
        {label}: {range(startAt, endAt)}
      </div>
      {diverges && (
        <>
          <div>Ganzer Termin: {range(apptStartAt, apptEndAt)}</div>
          <div>Die Zeit in Klammern ist der Beginn des ganzen Termins.</div>
        </>
      )}
    </>
  );

  return (
    <Tooltip title={tooltip}>
      <span className={className}>
        {startAt}
        {diverges && ` (${apptStartAt})`}
      </span>
    </Tooltip>
  );
};

export default AppointmentTime;
